import React, { useState, useEffect, useMemo } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { RegistrationEventRow } from "./RegistrationEventRow";
import { SortableOption } from "@/types/fieldsOptions";
import { useQuery } from "@tanstack/react-query";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "@/services/firebaseService";
import { initializeSortableOptions } from "@/services/sortableOptionService";

interface RegistrationEventsSectionProps {
  // Use a generic form type to allow reuse in bulk modal
  form: UseFormReturn<any>;
  markFieldAsModified?: (fieldName: string) => void;
  disabled?: boolean;
}

export const RegistrationEventsSection: React.FC<RegistrationEventsSectionProps> = ({
  form,
  markFieldAsModified = () => {},
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState<string[]>([]);
  
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "regEvents"
  });

  // Fetch registration event options from Firestore
  const { data: registrationEventOptions = [], error, isLoading, isError } = useQuery({
    queryKey: ["sortableOptions", "registrationEvents"],
    queryFn: async () => {
      const optionsRef = collection(db, "sortableOptions");
      const q = query(
        optionsRef,
        where("type", "==", "registrationEvents"),
        orderBy("sortOrder", "asc")
      );
      
      const snapshot = await getDocs(q);
      
      // If no registration events found, initialize them
      if (snapshot.empty) {
        try {
          await initializeSortableOptions(db);
          
          // Retry the query after initialization
          const retrySnapshot = await getDocs(q);
          
          const retryOptions = retrySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            } as SortableOption;
          });
          
          return retryOptions;
        } catch (initError) {
          console.error("[RegistrationEventsSection] Error during initialization:", initError);
          return [];
        }
      }
      
      const options = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as SortableOption;
      });
      
      return options;
    }
  });

  // Log the current state for debugging
  useEffect(() => {
    
    if (isError && error) {
      console.error("[RegistrationEventsSection] Error fetching registration event options:", error);
    }
  }, [isLoading, isError, error, registrationEventOptions]);

  // Initialize with one empty row if no events exist
  useEffect(() => {
    if (fields.length === 0) {
      append({
        eventKey: "",
        when: undefined,
        wasTimeProvided: false,
        note: ""
      });
    }
  }, [fields.length, append]);

  const handleAddRow = () => {
    if (disabled) return;
    append({
      eventKey: "",
      when: undefined,
      wasTimeProvided: false,
      note: ""
    });
    markFieldAsModified("regEvents");
  };

  const handleRemoveRow = (index: number) => {
    if (disabled) return;
    remove(index);
    markFieldAsModified("regEvents");
  };

  // Get the label for the accordion
  const accordionLabel = useMemo(() => {
    const current = (form.getValues("regEvents") || []) as any[];
    const validEvents = current.filter(e => e && e.when);
    
    if (validEvents.length === 0) {
      return "Registration dates (optional)";
    }
    return `Registration dates (${validEvents.length})`;
  }, [fields, form]);

  // Get default event key for first row
  const defaultEventKey = useMemo(() => {
    const openEvent = registrationEventOptions.find(opt => opt.id === "e_open");
    return openEvent?.id || "";
  }, [registrationEventOptions]);

  return (
    <Accordion 
      type="multiple" 
      value={isOpen} 
      onValueChange={setIsOpen}
      className="w-full rounded-md bg-muted p-2"
    >
      <AccordionItem value="registration-events" className="border rounded-md px-3">
        <AccordionTrigger className="hover:no-underline py-3">
          <span className="text-sm font-medium">{accordionLabel}</span>
        </AccordionTrigger>
        <AccordionContent className="pb-4">
          <div className="space-y-3">
            {/* Temporary debug info */}
            {process.env.NODE_ENV === 'development' && (
              <div className="p-2 bg-gray-100 rounded text-xs">
                <strong>Debug Info:</strong><br/>
                Loading: {isLoading ? 'Yes' : 'No'}<br/>
                Error: {isError ? error?.message || 'Unknown error' : 'None'}<br/>
                Options loaded: {registrationEventOptions.length}<br/>
                Options: {registrationEventOptions.map(opt => `${opt.id}:${opt.label}`).join(', ')}
              </div>
            )}
            
            {fields.map((field, index) => (
              <RegistrationEventRow
                key={field.id}
                form={form}
                index={index}
                isFirst={index === 0}
                isLast={index === fields.length - 1}
                registrationEventOptions={registrationEventOptions}
                defaultEventKey={index === 0 ? defaultEventKey : ""}
                onAdd={handleAddRow}
                onRemove={() => handleRemoveRow(index)}
                markFieldAsModified={markFieldAsModified}
                disabled={disabled}
              />
            ))}
            
            {/* Add button at the bottom if there are events */}
            {fields.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddRow}
                className="w-full mt-2"
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another registration date
              </Button>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}; 